import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Send, MapPin, HelpCircle } from "lucide-react";
import api from "@/lib/api";

const BARK_IMG = "/barkbark.png?v=3";
const RIG = {
  peek: "/rig/bb-full.png?v=9",               // peeking (idle) — closed mouth
  standFull: "/rig/bb-stand-full.png?v=2",    // standing closed (talking base)
  standRound: "/rig/bb-stand-round.png?v=2",  // standing 'oh'
  standWide: "/rig/bb-stand-wide.png?v=2",     // standing 'aah'
  eyes: "/rig/bb-eyes.png?v=1",               // optional closed-eyes blink overlay
};
const SESSION_KEY = "hat_barkbark_session";
const GREETED_KEY = "hat_barkbark_greeted";

const BUBBLE_TEXT = "Bark-Bark here if you need any help!";
const GREETING = "Hey there — I'm Bark-Bark! Need help finding something, or want directions to come meet me at the store?";

// Render Bark-Bark's reply: turn [label](url) markdown + bare /paths into clickable links.
function renderReply(text, onNavigate) {
  const nodes = [];
  let key = 0;
  const mdLink = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)/g;
  let last = 0;
  let m;
  const pushPlain = (str) => {
    const parts = str.split(/(\/[a-z0-9\-/]+)|(\*\*[^*]+\*\*)/gi).filter(Boolean);
    parts.forEach((part) => {
      if (/^\/[a-z0-9\-/]+$/i.test(part)) {
        nodes.push(<button key={key++} onClick={() => onNavigate(part)} className="text-copper underline underline-offset-2 hover:text-navy">{part}</button>);
      } else if (/^\*\*[^*]+\*\*$/.test(part)) {
        nodes.push(<strong key={key++} className="text-navy">{part.replace(/\*\*/g, "")}</strong>);
      } else {
        nodes.push(<span key={key++}>{part}</span>);
      }
    });
  };
  while ((m = mdLink.exec(text)) !== null) {
    if (m.index > last) pushPlain(text.slice(last, m.index));
    const [, label, url] = m;
    if (url.startsWith("/")) {
      nodes.push(<button key={key++} onClick={() => onNavigate(url)} className="text-copper underline underline-offset-2 hover:text-navy font-medium">{label}</button>);
    } else {
      nodes.push(<a key={key++} href={url} target="_blank" rel="noreferrer" className="text-copper underline underline-offset-2 hover:text-navy font-medium">{label}</a>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) pushPlain(text.slice(last));
  return nodes;
}

export default function BarkBark() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [bubble, setBubble] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [talking, setTalking] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || "");
  const scrollRef = useRef(null);
  const talkTimer = useRef(null);
  const rigRef = useRef(null);     // mascot container (for idle "accent" move)
  const roundRef = useRef(null);   // 'oh' mouth frame
  const wideRef = useRef(null);    // 'aah' mouth frame
  const eyesRef = useRef(null);    // blink overlay
  const [eyesReady, setEyesReady] = useState(false);

  // When idle, do a little "alive" move (head tilt / stretch) every few seconds.
  // Toggled directly on the DOM node (no state) so it never fights the viseme/talking state.
  useEffect(() => {
    if (talking) { rigRef.current?.classList.remove("bb-accent"); return; }
    let off;
    const loop = setInterval(() => {
      rigRef.current?.classList.add("bb-accent");
      off = setTimeout(() => rigRef.current?.classList.remove("bb-accent"), 1700);
    }, 6500);
    return () => { clearInterval(loop); if (off) clearTimeout(off); };
  }, [talking]);

  // Movie-style lip-sync via FRAME SWAPPING: closed / 'oh' / 'aah' are each a
  // COMPLETE dog whose own muzzle is drawn in that mouth position. Because every
  // frame shares identical body pixels, cross-fading their opacity morphs ONLY
  // the mouth — the dog's real mouth moves, integrated into the face (no overlay,
  // no floating graphic, no body flicker).
  useEffect(() => {
    const round = roundRef.current;
    const wide = wideRef.current;
    if (!round || !wide) return;
    const frames = { round, wide };
    const showOnly = (which, dur) => {
      Object.entries(frames).forEach(([k, el]) => {
        el.style.transition = `opacity ${dur}ms ease`;
        el.style.opacity = k === which ? "1" : "0";
      });
    };
    const closeMouth = (dur) => {
      [round, wide].forEach((el) => { el.style.transition = `opacity ${dur}ms ease`; el.style.opacity = "0"; });
    };
    if (!talking) { closeMouth(150); return; }
    let alive = true;
    let timer;
    let last = "";
    const phon = () => {
      if (!alive) return;
      if (Math.random() < 0.18) {                       // closed beat (consonant / word gap)
        closeMouth(80);
        last = "closed";
        timer = setTimeout(phon, 110 + Math.random() * 200);
        return;
      }
      // alternate shapes so the mouth visibly keeps moving
      let which = Math.random() < 0.5 ? "wide" : "round";
      if (which === last) which = which === "wide" ? "round" : "wide";
      showOnly(which, 65 + Math.random() * 45);
      last = which;
      const hold = 85 + Math.random() * 105;             // syllable length
      timer = setTimeout(phon, hold);
    };
    timer = setTimeout(phon, 20);
    return () => { alive = false; clearTimeout(timer); };
  }, [talking]);

  // Idle "alive" eye-blinks (only once the closed-eyes overlay is available).
  useEffect(() => {
    const el = eyesRef.current;
    if (!el || !eyesReady) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let alive = true;
    let loop, t1, t2, t3;
    const blink = () => {
      if (!alive) return;
      el.style.transition = "opacity 75ms ease-in";
      el.style.opacity = "1";
      t1 = setTimeout(() => {
        el.style.transition = "opacity 130ms ease-out";
        el.style.opacity = "0";
        if (Math.random() < 0.22) {                // occasional natural double-blink
          t2 = setTimeout(() => {
            el.style.transition = "opacity 75ms ease-in"; el.style.opacity = "1";
            t3 = setTimeout(() => { el.style.transition = "opacity 130ms ease-out"; el.style.opacity = "0"; }, 85);
          }, 190);
        }
      }, 95);
    };
    const schedule = () => { loop = setTimeout(() => { blink(); schedule(); }, 2800 + Math.random() * 4200); };
    schedule();
    return () => { alive = false; [loop, t1, t2, t3].forEach(clearTimeout); };
  }, [eyesReady]);

  // Show a greeting bubble once per browser session
  useEffect(() => {
    if (!sessionStorage.getItem(GREETED_KEY)) {
      const t = setTimeout(() => setBubble(true), 1800);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const openChat = () => {
    setBubble(false);
    sessionStorage.setItem(GREETED_KEY, "1");
    setOpen(true);
    if (messages.length === 0) {
      setMessages([{ role: "assistant", content: GREETING }]);
    }
  };

  const goNavigate = (path) => { setOpen(false); navigate(path); };

  // Keep Bark-Bark visibly "talking" for a moment after he replies.
  const startTalking = () => { setTalking(true); if (talkTimer.current) clearTimeout(talkTimer.current); };
  const stopTalkingSoon = () => {
    if (talkTimer.current) clearTimeout(talkTimer.current);
    talkTimer.current = setTimeout(() => setTalking(false), 2600);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    startTalking();
    try {
      const r = await api.post("/assistant/chat", { message: msg, session_id: sessionId || undefined });
      if (r.data.session_id && r.data.session_id !== sessionId) {
        setSessionId(r.data.session_id);
        localStorage.setItem(SESSION_KEY, r.data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Hmm, something went wrong on my end. Please try again in a moment, or visit /contact to reach the team." }]);
    } finally {
      setSending(false);
      stopTalkingSoon();
    }
  };

  return (
    <>
      {/* "Bark-Bark here if you need any help!" speech bubble — stays until the
          visitor opens the chat, then it goes away. */}
      {bubble && !open && (
        <button
          type="button"
          data-testid="barkbark-bubble"
          onClick={openChat}
          className="fixed z-40 bottom-[236px] sm:bottom-[300px] right-3 sm:right-6 max-w-[210px] text-left bg-white border border-tan shadow-xl shadow-navy/25 px-4 py-3 rounded-2xl rounded-br-sm hover:bg-sand transition-colors animate-[fadeIn_0.4s_ease]"
        >
          <span
            onClick={(e) => { e.stopPropagation(); setBubble(false); sessionStorage.setItem(GREETED_KEY, "1"); }}
            aria-label="Dismiss"
            data-testid="barkbark-bubble-dismiss"
            className="absolute -top-2 -right-2 w-6 h-6 bg-navy text-white rounded-full flex items-center justify-center hover:bg-charcoal cursor-pointer"
          ><X size={13} /></span>
          <p className="text-sm text-navy leading-snug font-serif">{BUBBLE_TEXT}</p>
          {/* little tail pointing down toward the dog */}
          <span className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-tan rotate-45" />
        </button>
      )}

      {/* Mascot — peeks around the corner when idle, smoothly stands up when chatting */}
      <button
        type="button"
        data-testid="barkbark-fab"
        onClick={() => (open ? setOpen(false) : openChat())}
        aria-label="Chat with Bark-Bark"
        className="fixed z-40 bottom-0 right-0 w-[200px] h-[230px] sm:w-[250px] sm:h-[300px] focus:outline-none"
      >
        <div className="bb-pose-stage w-full h-full">
          {/* Peeking pose (idle) */}
          <img
            src={RIG.peek}
            alt="Bark-Bark peeking around the corner"
            className={`bb-pose bb-peek drop-shadow-[0_14px_18px_rgba(0,0,0,0.5)] select-none ${open ? "bb-pose-out" : "bb-pose-in"}`}
          />
          {/* Standing pose (when chatting) — stacked mouth frames for lip-sync */}
          <div
            ref={rigRef}
            className={`bb-pose bb-stand drop-shadow-[0_14px_18px_rgba(0,0,0,0.5)] select-none ${open ? "bb-pose-in" : "bb-pose-out"} ${talking ? "bb-talking" : ""}`}
          >
            <img src={RIG.standFull} alt="Bark-Bark, your guide dog" className="bb-layer bb-base" />
            <img ref={roundRef} src={RIG.standRound} alt="" className="bb-layer bb-frame" />
            <img ref={wideRef} src={RIG.standWide} alt="" className="bb-layer bb-frame" />
            <img ref={eyesRef} src={RIG.eyes} alt="" className="bb-layer bb-eyes" onLoad={() => setEyesReady(true)} onError={() => setEyesReady(false)} />
          </div>
        </div>
      </button>

      {/* Chat panel */}
      {open && (
        <div data-testid="barkbark-panel" className="fixed z-50 bottom-4 right-[118px] sm:right-[210px] w-[calc(100vw-132px)] sm:w-[360px] h-[64vh] sm:h-[520px] bg-[#FAFAFA] border border-tan shadow-2xl shadow-navy/30 flex flex-col rounded-sm overflow-hidden">
          {/* Header */}
          <div className="bg-navy text-white px-4 py-3 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-white/10 border border-copper/40 overflow-hidden flex-shrink-0">
              <img src={RIG.standFull} alt="" className="w-full h-full object-cover object-top scale-[2.2] origin-top" />
            </div>
            <div className="flex-1">
              <div className="font-serif text-lg leading-none text-tan">Bark-Bark</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-1">Your High Achievers guide dog</div>
            </div>
            <button data-testid="barkbark-close" onClick={() => setOpen(false)} aria-label="Close" className="text-white/70 hover:text-tan"><X size={20} /></button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`barkbark-msg-${m.role}`} className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-sm ${m.role === "user" ? "bg-navy text-white" : "bg-white border border-tan/50 text-charcoal"}`}>
                  {m.role === "assistant" ? renderReply(m.content, goNavigate) : m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-tan/50 px-4 py-3 rounded-sm flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-copper rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-copper rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-copper rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}

            {/* Quick chips after greeting */}
            {messages.length === 1 && !sending && (
              <div className="flex flex-wrap gap-2 pt-1">
                <button data-testid="barkbark-chip-help" onClick={() => send("I need help finding the right product.")} className="inline-flex items-center gap-1.5 bg-sand border border-tan/60 text-navy text-xs px-3 py-2 hover:bg-tan/40 transition-colors">
                  <HelpCircle size={13} /> I need help
                </button>
                <button data-testid="barkbark-chip-directions" onClick={() => send("How do I get to your store?")} className="inline-flex items-center gap-1.5 bg-sand border border-tan/60 text-navy text-xs px-3 py-2 hover:bg-tan/40 transition-colors">
                  <MapPin size={13} /> Directions to the store
                </button>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-tan/50 p-3 flex items-center gap-2 bg-white">
            <input
              data-testid="barkbark-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Bark-Bark anything…"
              className="flex-1 border border-tan/50 px-3 py-2.5 text-sm bg-[#FAFAFA] focus:border-copper focus:outline-none"
            />
            <button data-testid="barkbark-send" type="submit" disabled={sending || !input.trim()} className="bg-copper text-navy p-2.5 hover:bg-[#E5C158] transition-colors disabled:opacity-40">
              <Send size={18} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
